import type React from "react";
import { forwardRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type {
  IStreamData,
  IThumbnailTemplate,
} from "../../../../../types/thumbnail-template";
import { tokens } from "../../../../../theme";

import muzukuru from "../../../../../assets/muzukuru.png";
import muzukuruBlack from "../../../../../assets/muzukurublack.png";
import oldMutuallogo from "../../../../../assets/old-mutual.png";
import firstmutuallogo from "../assets/firstmutual.png";
import {
  formatDateToLongString,
  formatLifespan,
  formatTime,
} from "../../../../../utils/FormatDate";

interface FuneralTemplateProps {
  streamData: IStreamData;
  template: IThumbnailTemplate;
  style?: React.CSSProperties;
}

const FuneralTemplate = forwardRef<HTMLDivElement, FuneralTemplateProps>(
  ({ streamData, template, style }, ref) => {
    const deceasedName =
      streamData?.bookingData?.booking_type === "FUNERAL" ||
      streamData?.bookingData?.booking_type === "FUNERAL_HOME"
        ? `${streamData?.bookingData?.deceased_name || ""} ${
            streamData?.bookingData?.deceased_second_name || ""
          } ${streamData?.bookingData?.deceased_surname || ""}`.trim()
        : `${streamData?.booking?.deceased?.[0]?.first_name || ""} ${
            streamData?.booking?.deceased?.[0]?.second_name || ""
          } ${streamData?.booking?.deceased?.[0]?.surname || ""}`.trim();

    const formattedDate = formatDateToLongString(streamData?.date);
    const formattedTime = formatTime(streamData?.shoot_from || "");
    const location = streamData?.location || streamData.service?.venue?.name;
    const address = `${streamData.address || ""}`;
    const lifespan = formatLifespan(
      streamData?.bookingData?.deceased_dob,
      streamData?.bookingData?.deceased_dod,
    );

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const textColor = template?.them_color || "#ffffff";
    const logoSrc = textColor === "#000000" ? muzukuruBlack : muzukuru;

    const isOldMutual = /old mutual/i.test(streamData?.location ?? "");
    const isFirstMutual = /first mutual/i.test(streamData?.location ?? "");

    const person_image =
      streamData?.booking?.deceased?.[0]?.person_image ||
      streamData.bookingData?.deceased_photo_url ||
      streamData.bookingData?.deceased_picture_url ||
      "";

    const showAvatar = !!person_image;

    return (
      <Box
        ref={ref}
        sx={{
          position: "relative",
          backgroundImage: `url(${template.background_image})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          color: textColor,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",

          fontFamily: '"Montserrat", sans-serif !important',

          "& *": {
            fontFamily: '"Montserrat", sans-serif !important',
          },

          ...style,
        }}
      >
        <Box
          sx={{
            padding: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "1280px",
              height: "720px",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "50px",
            }}
          >
            {/* Left Section */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "720px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {showAvatar ? (
                  <Box
                    sx={{
                      width: "400px",
                      height: "400px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: `4px solid ${textColor}`,
                    }}
                  >
                    <img
                      src={person_image || "/placeholder.svg"}
                      alt="Deceased"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = "";
                      }}
                    />
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: "2.5em",
                        color: textColor,
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      In Loving Memory Of
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "3em",
                        color: textColor,
                        textAlign: "center",
                        fontWeight: 700,
                      }}
                    >
                      {deceasedName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "2em",
                        color: textColor,
                        textAlign: "center",
                        fontStyle: "italic",
                      }}
                    >
                      {lifespan}
                    </Typography>
                  </>
                )}

                {isOldMutual || isFirstMutual ? (
                  <>
                    <Typography
                      sx={{
                        fontSize: "25px",
                        color: textColor,
                        textTransform: "uppercase",
                        mt: 2,
                      }}
                    >
                      Service provided by :
                    </Typography>

                    <img
                      src={
                        isOldMutual
                          ? oldMutuallogo
                          : firstmutuallogo || "/placeholder.svg"
                      }
                      alt={
                        isOldMutual ? "Old Mutual Logo" : "First Mutual Logo"
                      }
                      style={{ width: "350px" }}
                    />
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "5px",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          height: "2.2px",
                          backgroundColor: textColor,
                          maxWidth: "100px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "30px",
                          color: textColor,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        Broadcast by
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: "2.2px",
                          backgroundColor: textColor,
                          maxWidth: "100px",
                        }}
                      />
                    </Box>

                    <img
                      src={logoSrc || "/placeholder.svg"}
                      alt="Logo"
                      style={{
                        width: "350px",
                        marginTop: "-25px",
                      }}
                    />
                  </>
                )}
              </Box>
            </Box>

            {/* Right Section */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "576px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "30px",
                    letterSpacing: "0.05em",
                    color: textColor,
                    fontStyle: "italic",
                    marginTop: "-50px",
                    marginBottom: "20px",
                    fontWeight: "bold",
                  }}
                >
                  In Loving Memory of
                </Typography>

                <Typography
                  sx={{
                    fontSize: "3.8em",
                    marginTop: "-10px",
                    marginBottom: 0,
                    fontWeight: 700,
                    color: textColor,
                    textAlign: "center",
                    width: "100%",
                    lineHeight: 1.1,
                  }}
                >
                  {deceasedName}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "15px 0 25px 0",
                  }}
                >
                  <Typography
                    sx={{
                      backgroundColor: colors.neutral[50],
                      color: colors.neutral[700],
                      borderTopRightRadius: "10px",
                      borderBottomLeftRadius: "10px",
                      fontSize: "2.5em",
                      fontWeight: 700,
                      padding: "0 5px",
                    }}
                  >
                    LIVE
                  </Typography>
                  <Typography
                    sx={{
                      color: textColor,
                      fontSize: "2.5em",
                      fontWeight: 700,
                    }}
                  >
                    STREAM
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: "3em",
                    color: textColor,
                    fontWeight: 500,
                    marginBottom: "-5px",
                  }}
                >
                  {formattedDate}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "3em",
                    color: textColor,
                    fontWeight: 700,
                    marginBottom: "-24px",
                    marginTop: "18px",
                  }}
                >
                  {formattedTime}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "25px",
                    color: textColor,
                    textTransform: "uppercase",
                    margin: "30px 0 -5px 0",
                  }}
                >
                  BROADCASTING LIVE FROM
                </Typography>
                <Typography
                  sx={{
                    fontSize: "3em",
                    color: textColor,
                    textAlign: "center",
                    mb: 2,
                    lineHeight: 1.1,
                    fontWeight: 500,
                  }}
                >
                  {location}
                  <br /> {address}
                </Typography>
                {isOldMutual ||
                  (isFirstMutual && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "5px",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            height: "2.2px",
                            backgroundColor: textColor,
                            maxWidth: "100px",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "25px",
                            color: textColor,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          broadcast by
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            height: "2.2px",
                            backgroundColor: textColor,
                            maxWidth: "100px",
                          }}
                        />
                      </Box>
                      <img
                        src={logoSrc || "/placeholder.svg"}
                        alt="Logo"
                        style={{
                          width: "350px",
                          marginTop: "-25px",
                        }}
                      />
                    </>
                  ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

FuneralTemplate.displayName = "FuneralTemplate";
export default FuneralTemplate;
